/** Extract logline and synopsis for the scorecard page header. */

const BOILERPLATE_PATTERNS = [
  /^for\s+\*?.+\*?\.?\s*$/i,
  /^an analysis of the screenplay\s+\*?.+\*?\.?\s*$/i,
  /^analysis of the screenplay\s+\*?.+\*?\.?\s*$/i,
  /^this (?:analysis|report) (?:is )?for\s+/i,
];

function normaliseWhitespace(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function stripMarkdownEmphasis(text: string): string {
  return text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/\*(.+?)\*/g, "$1").trim();
}

export function isBoilerplateLogline(text: string, projectName?: string): boolean {
  const t = normaliseWhitespace(stripMarkdownEmphasis(text));
  if (!t || t.length < 20) return true;
  if (BOILERPLATE_PATTERNS.some((pat) => pat.test(t))) return true;
  if (projectName) {
    const slug = projectName.replace(/_/g, " ").toLowerCase();
    const compact = t.toLowerCase();
    if (
      (compact === slug ||
        compact === `for ${slug}.` ||
        compact === `for ${slug}` ||
        /^for\s+\*?/.test(t)) &&
      t.length < 120 &&
      !t.includes(",")
    ) {
      return true;
    }
  }
  return false;
}

/** Scan any markdown for the first substantive logline block. */
export function parseLoglineFromAnyMarkdown(markdown: string): string {
  if (!markdown?.trim()) return "";

  const patterns = [
    /\*\*Logline:?\*\*\s*\n+([\s\S]*?)(?=\n\s*(?:\*\*[A-Za-z]|\#{1,3}\s|- [A-Z]|\n\n\*\*)|\Z)/gi,
    /(?:^|\n)\s*Logline\s*:\s*\n+([\s\S]*?)(?=\n\s*(?:\*\*[A-Za-z]|\#{1,3}\s)|\Z)/gi,
    /(?:^|\n)\s*Logline\s*:\s*([^\n]+)/gi,
  ];

  for (const pat of patterns) {
    const re = new RegExp(pat.source, pat.flags);
    let m: RegExpExecArray | null;
    while ((m = re.exec(markdown)) !== null) {
      const raw = (m[1] ?? "").split(/\n{2,}/)[0] ?? "";
      const line = normaliseWhitespace(stripMarkdownEmphasis(raw));
      if (line && line !== "—" && line !== "-" && line.length >= 20) {
        return line;
      }
    }
  }
  return "";
}

/** Parse logline from blueprint scorecard markdown (`**Logline:**` block). */
export function parseLoglineFromScorecardMarkdown(markdown: string): string {
  return parseLoglineFromAnyMarkdown(markdown);
}

function isSynopsisNoiseParagraph(text: string): boolean {
  if (!text || text.length <= 40) return true;
  if (isBoilerplateLogline(text)) return true;
  if (/^narrative type/i.test(text)) return true;
  if (/^structural inputs/i.test(text)) return true;
  if (/^3\.2\./i.test(text)) return true;
  if (/^single-strand/i.test(text)) return true;
  if (/^––/.test(text)) return true;
  if (/^AGENT [A-Z]/i.test(text)) return true;
  return false;
}

function formatSynopsisBody(raw: string): string {
  if (!raw?.trim()) return "";
  const paragraphs = raw
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p && !/^––+$/.test(p))
    .map((p) => normaliseWhitespace(stripMarkdownEmphasis(p)))
    .filter((p) => !isSynopsisNoiseParagraph(p));
  return paragraphs.join("\n\n");
}

/** Parse synopsis from Agent U / scorecard markdown (V7 structural synopsis format). */
export function parseSynopsisFromAnyMarkdown(markdown: string): string {
  if (!markdown?.trim()) return "";

  const patterns = [
    /3\.2\.3\s+Structural Synopsis[^\n]*\n+([\s\S]*?)(?=\n\s*3\.2\.4\s+Logline|\n\s*(?:\*\*)?Logline(?:\*\*)?\s*[:\s]|\n––{3,}|\n##\s|\Z)/i,
    /(?:^|\n)\s*(?:\*\*)?Synopsis(?:\*\*)?\s*[:\s]*\n+([\s\S]*?)(?=\n\s*(?:\*\*)?Logline|\n\s*3\.2\.4\s+Logline|\n#{1,3}\s|\Z)/i,
    /(?:^|\n)\s*(?:\*\*)?Market(?:-|\s*)Facing Synopsis(?:\*\*)?\s*\n+([\s\S]*?)(?=\n\s*(?:\*\*)?Logline|\n#{1,3}\s|\Z)/i,
    /\*\*Synopsis:?\*\*\s*\n+([\s\S]*?)(?=\n\s*\*\*[A-Za-z]|\n\n\*\*|\Z)/i,
  ];

  for (const pat of patterns) {
    const m = markdown.match(pat);
    if (m?.[1]) {
      const formatted = formatSynopsisBody(m[1]);
      if (formatted) return formatted;
    }
  }
  return "";
}

/** Parse Agent U (Synopsis & Logline) block from full blueprint markdown. */
export function parseAgentUSection(fullMarkdown: string): { synopsis: string; logline: string } {
  if (!fullMarkdown?.trim()) return { synopsis: "", logline: "" };

  const sectionMatch = fullMarkdown.match(
    /##\s*U\.?\s*Synopsis\s*(?:&|and)\s*Logline\s*\n+([\s\S]*?)(?=\n##\s*[A-Z0-9]\.|\Z)/i
  );
  const block = sectionMatch?.[1]?.trim() ?? fullMarkdown;

  const logline = parseLoglineFromAnyMarkdown(block);
  let synopsis = parseSynopsisFromAnyMarkdown(block);

  if (!synopsis && logline) {
    const idx = block.search(/(?:^|\n)\s*(?:\*\*)?Logline(?:\*\*)?|3\.2\.4\s+Logline/i);
    if (idx > 0) synopsis = formatSynopsisBody(block.slice(0, idx));
  }

  return { synopsis, logline };
}

export interface ScorecardHeaderContent {
  logline: string;
  synopsis: string;
}

export function resolveScorecardHeaderContent(opts: {
  projectName?: string;
  scorecardLogline?: string | null;
  scorecardSynopsis?: string | null;
  scorecardMarkdown?: string;
  summaryMarkdown?: string;
  fullMarkdown?: string;
}): ScorecardHeaderContent {
  const projectName = opts.projectName ?? "";
  const combinedMarkdown = [
    opts.scorecardMarkdown,
    opts.summaryMarkdown,
    opts.fullMarkdown,
  ]
    .filter(Boolean)
    .join("\n\n");

  const fromAnywhere = parseLoglineFromAnyMarkdown(combinedMarkdown);
  const fromAgentU = parseAgentUSection(opts.fullMarkdown ?? combinedMarkdown);

  const candidates = [
    fromAnywhere,
    fromAgentU.logline,
    parseLoglineFromScorecardMarkdown(opts.scorecardMarkdown ?? ""),
    opts.scorecardLogline?.trim() ?? "",
  ].filter(Boolean);

  let logline = "";
  for (const c of candidates) {
    if (!isBoilerplateLogline(c, projectName)) {
      logline = c;
      break;
    }
  }

  let synopsis =
    (opts.scorecardSynopsis?.trim() && opts.scorecardSynopsis.trim() !== "—"
      ? opts.scorecardSynopsis.trim()
      : "") ||
    parseSynopsisFromAnyMarkdown(combinedMarkdown) ||
    fromAgentU.synopsis;

  if (!synopsis && opts.fullMarkdown) {
    const exec = opts.fullMarkdown.match(
      /###\s*Executive Summary\s*\n+([\s\S]*?)(?=\n###\s|\n##\s|\Z)/i
    );
    if (exec?.[1]) {
      const para = normaliseWhitespace(stripMarkdownEmphasis(exec[1].split("\n\n")[0] ?? ""));
      if (para && !isBoilerplateLogline(para, projectName)) synopsis = para;
    }
  }

  return { logline, synopsis };
}
